import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { Button, Tooltip, Typography } from "@mui/material";
import { TableSortDirection } from "../../../common/logic/TableSortUtils";
import { ReactNode, useEffect, useRef, useState } from "react";
import SwapVertIcon from '@mui/icons-material/SwapVert';
import { PaginatedData } from "../../../hooks/PaginationHooks";
import { TableColumnHeaderWrapper } from './TableElements.styles';
import { theme } from '../../../theme';
import { useProcessingRequest } from '../../../store/selectors/tableManagementSelector';

interface Props {
    label: string;
    isSorted?: boolean;
    fieldName?: string;
    prevIcon?: ReactNode;
    firstCol?: boolean;
    lastCol?: boolean;
    stickyFirstCol?: boolean;
    paginatedData?: PaginatedData<any>
}

export default function ServerSideTableColumnHeader(props: Props) {
    const processingRequest = useProcessingRequest();
    const [sortDirection, setSortDirection] = useState<TableSortDirection | null>();
    const labelRef = useRef<HTMLElement | null>(null);
    const [showLabelTooltip, setShowLabelTooltip] = useState(false);

    useEffect(() => {
        if (props.fieldName !== props.paginatedData?.paginationParameters.sort) {
            setSortDirection(null);
        }
        else {
            setSortDirection(props.paginatedData?.paginationParameters.order);
        }

    }, [
        props.paginatedData?.paginationParameters.order,
        props.paginatedData?.paginationParameters.sort,
        props.fieldName
    ]);

    useEffect(() => {
        function checkIfLabelIsTruncated() {
            const labelElement = labelRef.current;
            if (!labelElement) {
                setShowLabelTooltip(false);
                return;
            }

            setShowLabelTooltip(labelElement.scrollWidth > labelElement.clientWidth);
        };

        checkIfLabelIsTruncated();

        const labelElement = labelRef.current;
        if (!labelElement || typeof ResizeObserver === "undefined") return;

        const resizeObserver = new ResizeObserver(checkIfLabelIsTruncated);
        resizeObserver.observe(labelElement);

        return () => {
            resizeObserver.disconnect();
        };
    }, [props.label, sortDirection, props.isSorted]);

    function toggleSort() {
        if (!props.paginatedData?.handleOnSort || !props.fieldName) return;

        if (sortDirection == null) {
            props.paginatedData?.handleOnSort("asc", props.fieldName);
        } else if (sortDirection === "asc") {
            props.paginatedData?.handleOnSort("desc", props.fieldName);
        } else {
            props.paginatedData?.handleOnSort(null, null);
        }
    }

    return <TableColumnHeaderWrapper
        style={{
            ...(props.firstCol && props.stickyFirstCol && {
                position: "sticky",
                left: 0,
                top: 0,
                zIndex: 4,
                backgroundColor: theme.palette.background.paper,
                boxShadow: `2px 0 4px -2px ${theme.palette.grey[300]}`,
                filter: processingRequest ? "blur(2px)" : "none",
            }),
            paddingLeft: theme.spacing(2),
            ...(props.lastCol && { paddingRight: "25px" }),
        }}
    >
        <Tooltip
            title={props.label}
            disableHoverListener={!showLabelTooltip}
            disableFocusListener={!showLabelTooltip}
            disableTouchListener={!showLabelTooltip}
        >
            <Typography 
                ref={labelRef}
                style={{
                    flex: 1,
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    fontWeight: sortDirection ? "bold" : "normal",
                    color: sortDirection ? theme.palette.primary.main : "inherit"
                }}
                display={"inline-block"}
            >
                {props.label}
            </Typography>
        </Tooltip>

        {props.isSorted && sortDirection == null && <Button onClick={toggleSort}>
            <SwapVertIcon sx={{ fontSize: "1.5rem" }} />
        </Button>
        }
        {props.isSorted && sortDirection === "asc" && <Button onClick={toggleSort}>
            <ArrowDownwardIcon sx={{ fontSize: "1.5rem" }} />
        </Button>
        }
        {props.isSorted && sortDirection === "desc" && <Button onClick={toggleSort}>
            <ArrowUpwardIcon sx={{ fontSize: "1.5rem" }} />
        </Button>}
    </TableColumnHeaderWrapper>;
};
